import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Mail, 
  Search, 
  Zap, 
  Calendar, 
  Clock, 
  CheckCircle, 
  Linkedin,
  Target,
  Award,
  Building,
  BarChart,
  Lightbulb,
  MessageSquare,
  ArrowRight,
  TrendingUp
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function EducationAssessmentSystem() {
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Users className="w-16 h-16 text-golden animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Education Assessment <span className="text-golden">System</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Automated Lead Generation System
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              A comprehensive lead generation solution designed specifically for Education Assessment System to connect with educational institutions and decision-makers at scale, driving growth and adoption of your assessment solutions.
            </p>
          </div>
        </motion.div>

        {/* Introduction Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">The Automated Lead Generation System</h2>
          <p className="text-gray-300 mb-6">
            Our system leverages cutting-edge automation to deliver massive personalized outreach campaigns, sending up to <span className="text-golden font-semibold">2,000 highly targeted emails daily</span> to educational institutions and administrators who need comprehensive assessment solutions.
          </p>
          <p className="text-gray-300 mb-6">
            What makes this approach different is the level of personalization. We don't just send emails to random contacts—we specifically reach out to institutions actively seeking to improve their assessment processes, ensuring our outreach is relevant and timely.
          </p>
          <p className="text-gray-300">
            The system works 24/7, continuously generating leads while you focus on your core business activities. It's like having an entire business development team working around the clock, but at a fraction of the cost.
          </p>
        </MouseFollowEffect>

        {/* System Components */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-8 text-center">System Components</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Linkedin className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">LinkedIn Sales Navigator</h3>
                  <p className="text-gray-300">
                    We use LinkedIn Sales Navigator to identify and target the perfect prospects for Education Assessment System. This powerful tool allows us to filter by institution type, size, role, and more, ensuring we're reaching decision-makers in education.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Zap className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Make.com</h3>
                  <p className="text-gray-300">
                    Make.com serves as the automation backbone of our system, connecting all components and orchestrating the entire workflow. It handles everything from data processing to campaign management, ensuring seamless operation.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Mail className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Instantly.ai</h3>
                  <p className="text-gray-300">
                    Instantly.ai handles the email delivery with industry-leading deliverability rates. It manages email warming, sending schedules, and follow-ups automatically. The platform's advanced analytics provide real-time insights into campaign performance.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>

            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6">
              <div className="flex items-start mb-4">
                <Search className="w-10 h-10 text-golden mr-4 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold mb-2">Any Mail Finder</h3>
                  <p className="text-gray-300">
                    This specialized tool helps us discover verified email addresses for your target prospects with exceptional accuracy. It ensures we're reaching the right people through their preferred communication channel.
                  </p>
                </div>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Education Industry Specifics */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Tailored for Education Assessment</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Industry-Specific Targeting</h3>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Target Institutions:</strong> K-12 Schools, School Districts, Universities, Educational Boards</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Decision Makers:</strong> Superintendents, Principals, Assessment Coordinators, Department Heads</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Institution Size:</strong> 500+ students with active assessment needs</span>
                  </li>
                  <li className="flex items-start">
                    <Target className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span><strong>Geographic Focus:</strong> United States and International Markets</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <img 
                    src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?auto=format&fit=crop&q=80" 
                    alt="Education Assessment" 
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-4 text-golden">Education-Focused Messaging</h3>
                <p className="text-gray-300 mb-4">
                  Our messaging strategy addresses the specific pain points that drive educational institutions to seek assessment solutions:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Inefficient assessment processes and data management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Need for standardized evaluation methods</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Compliance with educational standards</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Data-driven decision making requirements</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span>Student performance tracking and reporting</span>
                  </li>
                </ul>
                <div className="mt-6">
                  <img 
                    src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?auto=format&fit=crop&q=80" 
                    alt="Education Technology" 
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Your Investment</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Complete lead generation system setup</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Custom targeting for your ideal clients</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Professional content creation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Automated follow-up sequences</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Real-time analytics dashboard</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing campaign optimization</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Weekly performance reports</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Investment Structure</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">One-time setup fee:</span>
                      <span className="text-golden font-bold">$2,300</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Performance fee:</span>
                      <span className="text-golden font-bold">8% per successful deal</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-2 text-center">ROI Highlights</h3>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-golden">300%+</p>
                      <p className="text-sm text-gray-300">Potential ROI</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">30 Days</p>
                      <p className="text-sm text-gray-300">Average Payback</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Ready to Transform Your Lead Generation?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Let's discuss how our lead generation system can work for Education Assessment System and start delivering qualified leads within days.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/lead-gen-setup-call"
                  className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Implementation Call
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Competitive Advantage */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Your Competitive Advantage</h2>
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <Award className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Market Leadership</h3>
                <p className="text-gray-300">
                  Position your assessment system as the go-to solution in your markets by reaching more potential clients than your competitors.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <Building className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Scalable Growth</h3>
                <p className="text-gray-300">
                  Expand into new educational markets with a proven lead generation system that scales with your business goals.
                </p>
              </div>
              
              <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                <TrendingUp className="w-12 h-12 text-golden mb-4" />
                <h3 className="text-xl font-bold mb-2">Predictable Revenue</h3>
                <p className="text-gray-300">
                  Transform your business development from unpredictable to consistent with a steady flow of qualified leads.
                </p>
              </div>
            </div>
            
            <div className="mt-8">
              <h3 className="text-xl font-bold mb-4 text-golden text-center">Client Success Stories</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "This lead generation system has completely transformed our business development process. We're now connecting with decision-makers we couldn't reach before, and our sales team is focused on closing deals rather than prospecting."
                  </p>
                  <p className="text-golden font-semibold">
                    — Regional Assessment Provider, 300% ROI in 60 days
                  </p>
                </div>
                
                <div className="bg-black/30 p-6 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-4">
                    "The quality of leads we're getting is remarkable. These are educational institutions actively looking for assessment solutions, and the personalized approach makes all the difference in getting responses from busy administrators."
                  </p>
                  <p className="text-golden font-semibold">
                    — National Education Technology Firm, 42 new clients in 90 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}